import {
  cx,
  FormField,
  TableControl,
  TableFilter,
  TableSearch,
  type TableToolbarSearch,
  TextInput,
} from '@croissants/ui';
import { createContext, type JSX, type ReactNode, useContext } from 'react';
import { useT } from '../i18n/index';

/**
 * Is this field sitting in a table toolbar rather than in a form?
 *
 * `FormField` stacks its label ABOVE its control, which is right in a form and
 * wrong in a filter bar: a labelled search box came out taller than the select
 * beside it, and the band above every table in this portal was ragged. The two
 * renderings are the same field with the same wiring, so the decision is taken
 * from POSITION rather than from a flag each of eleven screens would have to
 * pass - and pass consistently.
 */
const InToolbar = createContext(false);

/**
 * Thin wrappers over `FormField` from the design system.
 *
 * `FormField` owns the accessible wiring - label bound by id, hint and error via
 * `aria-describedby`, the error announced through a live region (PRD NFR-005).
 * These exist only so a screen with eleven filters does not repeat the render
 * prop eleven times; every one of them still goes through `FormField`, so none
 * can accidentally skip that wiring.
 */

interface BaseFieldProps {
  label: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  /** Edited-but-unsaved: the form owns the baseline and computes this. */
  dirty?: boolean;
  className?: string;
  id?: string;
}

export interface SelectOption {
  readonly value: string;
  readonly label: string;
}

export function SelectField({
  label,
  hint,
  error,
  required,
  dirty,
  className,
  id,
  value,
  onChange,
  options,
  includeEmpty,
  emptyLabel,
  aside,
}: BaseFieldProps & {
  value: string;
  onChange: (next: string) => void;
  options: readonly SelectOption[];
  /** Adds a leading "all"/"none" option; its value is the empty string. */
  includeEmpty?: boolean;
  emptyLabel?: string;
  /**
   * One muted word on the select's own line, after it (a case's "Suggested").
   * Pass `''` while there is none to show: the select then keeps its place in
   * the tree, so the word coming and going never remounts it under the cursor.
   */
  aside?: string;
}): JSX.Element {
  const t = useT();
  const inToolbar = useContext(InToolbar);
  const items = (
    <>
      {includeEmpty === true ? <option value="">{emptyLabel ?? t('field.all')}</option> : null}
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </>
  );

  if (inToolbar) {
    return (
      <TableFilter
        label={typeof label === 'string' ? label : ''}
        value={value}
        {...(dirty === true ? { className: 'cib-dirty' } : {})}
        onChange={(event) => onChange(event.target.value)}
      >
        {items}
      </TableFilter>
    );
  }

  return (
    <FormField
      layout="row"
      label={label}
      dirty={dirty === true}
      {...(hint === undefined ? {} : { hint })}
      {...(error === undefined ? {} : { error })}
      {...(required === undefined ? {} : { required })}
      {...(required === true ? { requiredLabel: t('field.required') } : {})}
      {...(className === undefined ? {} : { className })}
      {...(id === undefined ? {} : { id })}
    >
      {(control) => {
        const asideId = `${control.id}-aside`;
        const select = (
          <select
            {...control}
            aria-describedby={
              aside === undefined || aside === ''
                ? control['aria-describedby']
                : [control['aria-describedby'], asideId].filter(Boolean).join(' ')
            }
            value={value}
            onChange={(event) => onChange(event.target.value)}
          >
            {includeEmpty === true ? (
              <option value="">{emptyLabel ?? t('field.all')}</option>
            ) : null}
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );
        return aside === undefined ? (
          select
        ) : (
          <span className="internal-select-aside">
            {select}
            <span id={asideId}>{aside}</span>
          </span>
        );
      }}
    </FormField>
  );
}

export function TextField({
  label,
  hint,
  error,
  required,
  dirty,
  className,
  id,
  value,
  onChange,
  type = 'text',
  placeholder,
  inputMode,
  readOnly,
}: BaseFieldProps & {
  value: string;
  onChange: (next: string) => void;
  /** Shown and not editable: a value the record no longer lets anyone change. */
  readOnly?: boolean;
  /* `email` and `tel` are what the pairing send dialog asks for: the mobile a
     PIN is texted to and the address a QR is mailed to (BAK-035). The type is
     the keyboard on a tablet and the browser's own first check, never the
     validation - that is the contract's `Email` and `PhoneE164`. */
  type?: 'text' | 'date' | 'number' | 'search' | 'email' | 'tel';
  placeholder?: string;
  inputMode?: 'text' | 'numeric' | 'decimal';
}): JSX.Element {
  const t = useT();
  const inToolbar = useContext(InToolbar);

  if (inToolbar) {
    const name = typeof label === 'string' ? label : '';
    /*
     * A search is the one filter that belongs on the RIGHT. It is identified by
     * its own type rather than by a prop, so a screen cannot put its search on
     * the left by forgetting to say where it goes.
     */
    if (type === 'search') {
      return (
        <div className="cib-toolbar__search">
          <TableSearch
            label={name}
            value={value}
            onValueChange={onChange}
            {...(placeholder === undefined ? {} : { placeholder })}
          />
        </div>
      );
    }
    return (
      <TableControl label={name}>
        {(control) => (
          <input
            {...control}
            className={cx(control.className, dirty === true && 'cib-dirty')}
            type={type}
            value={value}
            onChange={(event) => onChange(event.target.value)}
            {...(placeholder === undefined ? {} : { placeholder })}
            {...(inputMode === undefined ? {} : { inputMode })}
          />
        )}
      </TableControl>
    );
  }

  return (
    <FormField
      layout="row"
      label={label}
      dirty={dirty === true}
      {...(hint === undefined ? {} : { hint })}
      {...(error === undefined ? {} : { error })}
      {...(required === undefined ? {} : { required })}
      {...(required === true ? { requiredLabel: t('field.required') } : {})}
      {...(className === undefined ? {} : { className })}
      {...(id === undefined ? {} : { id })}
    >
      {(control) => (
        <TextInput
          {...control}
          type={type}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          {...(placeholder === undefined ? {} : { placeholder })}
          {...(inputMode === undefined ? {} : { inputMode })}
          {...(readOnly === true ? { readOnly: true } : {})}
        />
      )}
    </FormField>
  );
}

export function TextAreaField({
  label,
  hint,
  error,
  required,
  dirty,
  className,
  id,
  value,
  onChange,
  rows = 3,
  layout = 'row',
  readOnly,
  placeholder,
}: BaseFieldProps & {
  value: string;
  onChange: (next: string) => void;
  rows?: number;
  placeholder?: string;
  /** Shown and not editable, as `TextField`'s. */
  readOnly?: boolean;
  /** `stack` puts the label above the box - inside a dialog, where a row has no room. */
  layout?: 'row' | 'stack';
}): JSX.Element {
  const t = useT();
  return (
    <FormField
      layout={layout}
      label={label}
      dirty={dirty === true}
      {...(hint === undefined ? {} : { hint })}
      {...(error === undefined ? {} : { error })}
      {...(required === undefined ? {} : { required })}
      {...(required === true ? { requiredLabel: t('field.required') } : {})}
      {...(className === undefined ? {} : { className })}
      {...(id === undefined ? {} : { id })}
    >
      {(control) => (
        <textarea
          {...control}
          rows={rows}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          {...(readOnly === true ? { readOnly: true } : {})}
          {...(placeholder === undefined ? {} : { placeholder })}
        />
      )}
    </FormField>
  );
}

/**
 * A checkbox is the one control `FormField` does not fit. It follows the same
 * row grammar as everything else on a form now - label on the left, the state
 * against the right edge - and still binds label to input and describes it,
 * which is the part that matters.
 */
export function CheckboxField({
  label,
  hint,
  checked,
  onChange,
  dirty,
  id,
}: {
  label: ReactNode;
  hint?: ReactNode;
  checked: boolean;
  onChange: (next: boolean) => void;
  /** Edited-but-unsaved: the form owns the baseline and computes this. */
  dirty?: boolean;
  id: string;
}): JSX.Element {
  const hintId = `${id}-hint`;
  /*
   * The platform's edited-but-unsaved marker tints the CONTROL's own ground,
   * and `cib-dirty` in packages/ui excludes a checkbox from that by name: a
   * native tick box paints its own box, so a background under it is invisible.
   * The row is what carries the tint here instead - the same amber, on the
   * thing that IS visible - so a ticked-but-unsaved switch reads as pending
   * exactly like an edited text box on the same form.
   */
  return (
    <div className={cx('internal-checkbox', dirty === true && 'internal-checkbox--dirty')}>
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        {...(hint === undefined ? {} : { 'aria-describedby': hintId })}
      />
      {hint === undefined ? null : (
        <p className="cib-field__hint" id={hintId}>
          {hint}
        </p>
      )}
    </div>
  );
}

/**
 * The same wiring as `CheckboxField`, drawn as a switch. For the states that
 * read as on/off rather than ticked/unticked - an item being on the menu - a
 * switch says "this is live" where a checkbox says "this is selected".
 */
export function ToggleField({
  label,
  hint,
  checked,
  onChange,
  id,
}: {
  label: ReactNode;
  hint?: ReactNode;
  checked: boolean;
  onChange: (next: boolean) => void;
  id: string;
}): JSX.Element {
  const hintId = `${id}-hint`;
  return (
    <div className="internal-checkbox">
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        className="cib-switch"
        type="checkbox"
        role="switch"
        aria-checked={checked}
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        {...(hint === undefined ? {} : { 'aria-describedby': hintId })}
      />
      {hint === undefined ? null : (
        <p className="cib-field__hint" id={hintId}>
          {hint}
        </p>
      )}
    </div>
  );
}

/**
 * The band above a table: filters on the left, search on the right, one row and
 * one height.
 *
 * Every field inside renders its toolbar form, decided by the context rather
 * than by eleven screens each remembering to ask for it. The screens were not
 * touched to get this - which is the point: the raggedness came from each of
 * them laying out its own controls, so the fix has to be in the one place they
 * all pass through.
 *
 * Handed a `useTable` result it draws the search box itself, because a search
 * that narrows the rows a screen already holds is the same three lines every
 * time. A screen whose box drives a SERVER parameter through its own state
 * keeps passing a `TextField type="search"` as a child instead - that one has
 * to be debounced, cleared and sent, which is the screen's business and not
 * this bar's.
 *
 * ONE OR THE OTHER, and this bar cannot check. `TableToolbar` takes its search
 * as a SLOT and can let an explicit one win; a bar whose filters are `children`
 * cannot look inside them, so passing `table` to a screen that already hands
 * down a `TextField type="search"` renders TWO boxes side by side, each in its
 * own right-aligned wrapper, and nothing fails. Most of the screens this portal
 * is about to convert have that child today - ask, rather than trusting a list
 * here that will rot:
 *
 *     git grep -ln 'type="search"' apps/internal-admin/src/screens
 *
 * Give a screen `table` and DELETE the child, in the same edit. What the box the
 * bar draws does guarantee is its NAME: `table` and `searchLabel` travel
 * together or not at all, so an unnamed box - `aria-label=""`, silently - does
 * not compile.
 *
 * The box itself is `TextField type="search"`, the same call a screen writes,
 * because two spellings of one control in one file is how they drift.
 */
type FilterBarProps = { children?: ReactNode } & (
  | {
      /** A `useTable` result: the bar draws the search box on the right from it. */
      readonly table: TableToolbarSearch;
      /** Accessible name of that box. Already translated. */
      readonly searchLabel: string;
      readonly searchPlaceholder?: string;
    }
  | {
      readonly table?: undefined;
      readonly searchLabel?: undefined;
      readonly searchPlaceholder?: undefined;
    }
);

export function FilterBar(props: FilterBarProps): JSX.Element {
  // Read off `props` rather than destructured, so narrowing on `table` carries
  // `searchLabel` with it - destructuring separates them and loses the pair.
  return (
    <InToolbar.Provider value={true}>
      <div className="cib-toolbar">
        {props.children}
        {props.table === undefined ? null : (
          <TextField
            label={props.searchLabel}
            type="search"
            value={props.table.query}
            onChange={props.table.setQuery}
            {...(props.searchPlaceholder === undefined
              ? {}
              : { placeholder: props.searchPlaceholder })}
          />
        )}
      </div>
    </InToolbar.Provider>
  );
}

/**
 * ONE TYPED MONEY BOX, wherever a screen asks for an amount inside a row.
 *
 * The column heading, or the section above it, already names the figure, so the
 * field's own label is read by assistive technology only - and it names what the
 * amount is FOR too, because "Baker price" repeated forty times down a column is
 * not an accessible name.
 *
 * It lived beside the menu table until the set editor needed the same box for an
 * option's supplement (founder, 2026-09-01). Two boxes parsing euros two ways is
 * exactly the duplication `toMinorUnits` (@croissants/ui) exists to stop, so the box moved here
 * rather than being drawn a second time.
 *
 * `step` is what turns it into a STEPPED box - the browser's own up/down, on the
 * grid the caller names - and nothing else changes: the same field, the same
 * parsing, the same amber marker on an edit.
 */
export function Amount({
  label,
  name,
  value,
  dirty,
  error,
  placeholder,
  className = 'internal-menus__amount',
  step,
  min,
  disabled = false,
  onChange,
  onBlur,
}: {
  label: string;
  /** What the amount is for, appended to the label for the screen reader. */
  name?: string;
  value: string;
  dirty: boolean;
  error?: string;
  placeholder?: string;
  className?: string;
  /** In whole currency units. Its presence is what asks for the up/down. */
  step?: number;
  min?: number;
  disabled?: boolean;
  onChange: (next: string) => void;
  onBlur?: () => void;
}): JSX.Element {
  return (
    <FormField
      label={name === undefined ? label : `${label} - ${name}`}
      labelHidden
      dirty={dirty}
      className={className}
      {...(error === undefined ? {} : { error })}
    >
      {(control) => (
        <TextInput
          {...control}
          inputMode="decimal"
          disabled={disabled}
          value={value}
          {...(step === undefined ? {} : { type: 'number', step })}
          {...(min === undefined ? {} : { min })}
          {...(placeholder === undefined ? {} : { placeholder })}
          {...(onBlur === undefined ? {} : { onBlur })}
          onChange={(event) => onChange(event.target.value)}
        />
      )}
    </FormField>
  );
}

/**
 * A field that lives in a TABLE ROW rather than in a form.
 *
 * The column heading is the label, so the field's own is announced and not
 * drawn - `labelHidden`, which keeps the accessible wiring `FormField` owns and
 * loses only the visible text. Everything else is the same field: the amber
 * marker on an edit, the error, the required marker, all decided by the caller
 * against the loaded record exactly as a form's are.
 *
 * It exists so a screen editing a list in place does not re-implement that per
 * cell, and so a cell can never quietly drop the wiring by rendering a bare
 * `<input>` - which is what it would otherwise be.
 */
export function RowText({
  label,
  value,
  onChange,
  dirty,
  required,
  placeholder,
}: {
  label: ReactNode;
  value: string;
  onChange: (next: string) => void;
  dirty?: boolean;
  required?: boolean;
  placeholder?: string;
}): JSX.Element {
  const t = useT();
  return (
    <FormField
      label={label}
      labelHidden
      className="internal-rowfield"
      dirty={dirty === true}
      {...(required === undefined ? {} : { required })}
      {...(required === true ? { requiredLabel: t('field.required') } : {})}
    >
      {(control) => (
        <TextInput
          {...control}
          type="text"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          {...(placeholder === undefined ? {} : { placeholder })}
        />
      )}
    </FormField>
  );
}

/**
 * `RowText`'s sibling for a choice: a select that lives in a TABLE ROW, its
 * column heading the label, so the field's own is announced and not drawn.
 * `SelectField` cannot stand in for it - it is a full-width label-and-box row.
 */
export function RowSelect({
  label,
  value,
  onChange,
  options,
  className,
}: {
  label: ReactNode;
  value: string;
  onChange: (next: string) => void;
  options: readonly SelectOption[];
  className?: string;
}): JSX.Element {
  return (
    <FormField
      label={label}
      labelHidden
      dirty={false}
      {...(className === undefined ? {} : { className })}
    >
      {(control) => (
        <select {...control} value={value} onChange={(event) => onChange(event.target.value)}>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      )}
    </FormField>
  );
}
